<template>
  <div class="composite-picker">
    <ui-button
      class="composite-picker__button"
      icon="fas fa-cog"
      :planLimits="planLimits"
      :menu-props="{
        menuWidth: 220,
        target: 'wrapper',
        location: 'bottom end',
        offset: [0, 0],
      }"
      :menu-list="menuList"
    >
      <template #item="{ item }">
        <slot name="item" :item="item">
          <div class="list-container d-flex align-center justify-space-between flex-1">
            <div class="d-flex align-center">
              <div class="text">{{ item.title }}</div>
              <ui-tooltip-icon
                v-if="item.helpTooltip"
                preset="help"
                class="mb-4 ml-2"
                :text="item.helpTooltip"
              ></ui-tooltip-icon>
            </div>
            <div class="subtitle-container">
              <div v-if="item.subtitle" class="subtitle">{{ item.subtitle }}</div>
              <ui-icon
                v-if="item.icon"
                :disabled="item.disabled"
                :color="$colors.black"
                :icon="item.icon"
              ></ui-icon>
            </div>
          </div>
        </slot>
      </template>
    </ui-button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import query from '@/components/query/query-constructor';
import currencySigns from '@/components/helpers/currency-list';
import { useI18n, useColors, useCurrencyList } from '@/composables';
import { useUrl } from '@/composables/useUrl';
import { useTableQuery } from '@/composables/useTableQuery';
import { usePickerPushUrl } from '@/composables/usePickerPushUrl';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';

const props = defineProps<{
  planLimits?: keyof BillingLimits;
  isCustom?: boolean;
}>();

const { t } = useI18n();
const $colors = useColors();
const route = useRoute();
const store = useStore();
const { currencyListOrderedPreferred, currencyListOrdered } = useCurrencyList();
const { isLiveVisits, isReport } = useUrl();
const tableQueryApi = useTableQuery();
const { pushReportUrl } = usePickerPushUrl();

const pageSizeList = [50, 100, 200, 500, 1000];

const getCurrencyCode = (code: string) => `${currencySigns[code]} ${code}`;

const selectedCurrencyItem = computed(() =>
  currencyListOrdered.value.find(
    (item) => item.code === tableQueryApi.tableQueryForCurrentTab.value.currency,
  ),
);

const pickFromUrl = (param: string, fallback: unknown = null) => {
  const cache = query.decode(route.params.data as string);
  return typeof cache[param] !== 'undefined' ? cache[param] : fallback;
};

const handleSelectPageSize = (selectedLimit: number) => {
  if (tableQueryApi.tableQueryForCurrentTab.value.limit !== selectedLimit) {
    tableQueryApi.patchTableQueryField({ name: 'limit', value: selectedLimit });
    pushReportUrl();
    $emitter.emit('reset-pagination');
  }
};

const handleExport = () => {
  if (pickFromUrl('view') === 'tree' && pickFromUrl('searchBy') === 'tags') {
    $emitter.emit(
      'toast-warning',
      t(
        'This report will export to CSV in table format.<br>Downloaded data may differ from treeview report.<br>To preview in CSV, switch report to tableview.',
      ),
    );
  }
  $emitter.emit('open-dialog', { name: 'ExportCSV' });
};

const menuListCurrency = computed<Bemob.ListItem>(() => ({
  ...(selectedCurrencyItem.value && {
    title: t(selectedCurrencyItem.value.name),
    subtitle: getCurrencyCode(selectedCurrencyItem.value.code),
  }),
  subItems: currencyListOrderedPreferred.value.map(
    (item) =>
      ({
        title: item.name,
        subtitle: getCurrencyCode(item.code),
        click: () => handleSelectCurrency(item.code),
      }) as Bemob.ListItem,
  ),
  meta: {
    filter: true,
  },
}));

const menuListPageSize = computed<Bemob.ListItem>(() => ({
  title: t('Rows Per Page'),
  subtitle: String(tableQueryApi.tableQueryForCurrentTab.value.limit ?? ''),
  subItems: pageSizeList.map(
    (item) =>
      ({
        title: String(item),
        click: () => handleSelectPageSize(item),
      }) as Bemob.ListItem,
  ),
  hidden: isLiveVisits.value,
}));

const menuListExport = computed<Bemob.ListItem>(() => ({
  title: t('Export CSV'),
  icon: 'fas fa-file-download',
  click: handleExport,
}));

const menuList = computed(() =>
  ([menuListCurrency.value, menuListPageSize.value, menuListExport.value] as Bemob.ListItem[]).filter(
    ({ hidden }) => !hidden,
  ),
);

const applyDataFromUrl = () => {
  const routeParams = query.decode(route.params.data as string);
  const { currency, limit } = routeParams;
  tableQueryApi.patchTableQueryField({
    name: 'currency',
    value: currency || store.user.currencyId,
  });
  tableQueryApi.patchTableQueryField({
    name: 'limit',
    value: limit ? +limit : 50,
  });
};

const handleSelectCurrency = (code: string) => {
  tableQueryApi.patchTableQueryField({ name: 'currency', value: code });
  pushReportUrl();
  $emitter.emit('refresh-report');
};

const onBusReportTabAdded = () => {
  applyDataFromUrl();
};

onMounted(() => {
  $emitter.on('report-tab-added', onBusReportTabAdded);
  if (!isReport.value) {
    applyDataFromUrl();
  }
});

onBeforeUnmount(() => {
  $emitter.off('report-tab-added', onBusReportTabAdded);
});
</script>

<style scoped lang="scss">
.list-container {
  display: flex;
  flex: 1;
  padding: 0;

  .text {
    text-transform: capitalize;
    font-size: 13px;
  }
}

.subtitle-container {
  display: flex;

  .subtitle {
    font-size: 13px;
    font-weight: 700;
    margin-right: 4px;
  }
}
</style>
